import React, { useEffect, useMemo, useRef, useState } from 'react'
import useCurrentTeacher from 'common/context/TeacherContext'
import Spinner from 'common/tw/Spinner'
import { Flow, Term, Tier, Selection, FLOW_TYPES } from './types'
import { FLOW_CONFIG } from './constants'
import { trimPrice } from './lib/format'
import {
  entryFor,
  termFor,
  seatMinimum,
  buildPurchaseDetails,
  planNameFor,
} from './lib/planViewModel'
import useAccountFlows, { eligibleFlows } from './hooks/useAccountFlows'
import useLicenseCode from './hooks/useLicenseCode'
import useAccountStatus from './hooks/useAccountStatus'
import useSeatsUsed from './hooks/useSeatsUsed'
import usePrices from './hooks/usePrices'
import useRenewableLicense, {
  renewedEndsAt,
  renewableFor,
} from './hooks/useRenewableLicense'
import useScrollIntoView from './hooks/useScrollIntoView'
import usePageBackground from './hooks/usePageBackground'
import PageHeader from './components/PageHeader'
import FlowPlans from './components/FlowPlans'
import 'common/tw/tailwind.css'
import PurchasePanel from './components/PurchasePanel'
import OtherOptions from './components/OtherOptions'
import CustomPlanPanel from './components/CustomPlanPanel'
import submitCheckout, { submitRenewal } from './useCheckout'

const WELCOME_PATH = 'app/signup/teacher/welcome'

const GENERIC_SUBHEADING =
  'Unlock everything XtraMath has to offer. Cancel or change any time.'

function Loading() {
  return (
    <div className='flex justify-center p-12 text-brand'>
      <Spinner size={32} />
    </div>
  )
}

function UpgradeLicense() {
  const { currentTeacher, classrooms } = useCurrentTeacher()
  const [term, setTerm] = useState<Term>('annual')
  const [count, setCount] = useState<number | null>(null)
  const [selection, setSelection] = useState<Selection | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  usePageBackground()

  const lCode = useLicenseCode()
  const cameFrom = useRenewableLicense(currentTeacher, lCode)
  const [chosenFlow, setChosenFlow] = useState<Flow | null>(null)
  const accountFlows = useAccountFlows(currentTeacher, cameFrom)
  const flows = chosenFlow ? [chosenFlow] : accountFlows
  const switchTo = eligibleFlows(currentTeacher)
  const primaryFlow: Flow = flows[0] ?? 'teacher'
  const { hadPremium, hasActivePro, plan } = useAccountStatus(
    currentTeacher,
    primaryFlow
  )
  const seatsUsedFor = useSeatsUsed(currentTeacher, classrooms)
  const { annualPrices, sprintPrices } = usePrices(
    flows.length > 0,
    flows.some((f) => FLOW_CONFIG[f].hasTermToggle)
  )

  const selectedEntry = selection
    ? entryFor(selection.flow, selection.tier, term, annualPrices, sprintPrices)
    : undefined
  const selectedTerm = termFor(selection, term)

  const seats = useMemo<{ min: number; start: number } | null>(() => {
    if (!selection || !selectedEntry) return null
    const used = seatsUsedFor(selection.flow)
    if (used === null) return null
    return { min: seatMinimum(selection), start: seatMinimum(selection) }
  }, [selection, selectedEntry, seatsUsedFor])

  // Start a fresh seat count whenever the buyer switches plans.
  useEffect(() => setCount(null), [selection])

  // Derived, not stored: an effect keyed on seatMin misses a re-pick at the same minimum.
  const effectiveCount =
    seats === null ? null : Math.max(count ?? seats.start, seats.min)

  useScrollIntoView(panelRef, selection)

  if (!currentTeacher || !flows.length) return <Loading />

  const onlyFamily = flows.length === 1 && flows[0] === 'family'
  const familyPro = annualPrices?.[FLOW_TYPES.family.pro]
  const header = (
    <PageHeader
      statusLabel={plan.label}
      statusExpired={plan.expired}
      statusActive={plan.active}
      heading={FLOW_CONFIG[onlyFamily ? 'family' : primaryFlow].heading}
      subHeading={
        onlyFamily && familyPro
          ? `$${trimPrice(familyPro.price)} per year, up to ${
              familyPro.numSeats
            } children included.`
          : GENERIC_SUBHEADING
      }
    />
  )

  // Wait until every offered plan has both a price and a seat count.
  const ready = flows.every(
    (f) =>
      !!entryFor(f, 'pro', term, annualPrices, sprintPrices) &&
      seatsUsedFor(f) !== null
  )
  if (!ready) return <Loading />

  const details =
    selection && selectedEntry && effectiveCount !== null
      ? buildPurchaseDetails(
          selection,
          selectedEntry,
          effectiveCount,
          selectedTerm,
          seatsUsedFor(selection.flow) ?? 0
        )
      : null

  // Same object when the plan has not changed, so the reset above leaves the count alone.
  const handleSelect = (flow: Flow, tier: Tier) =>
    setSelection((prev) =>
      prev && prev.flow === flow && prev.tier === tier ? prev : { flow, tier }
    )

  const handleCheckout = () => {
    if (!selection || !selectedEntry) return
    const types = FLOW_TYPES[selection.flow]
    const buying = selection.tier === 'premium' ? types.premium : types.pro
    const extending = renewableFor(currentTeacher, buying)
    if (extending) {
      submitRenewal({
        tCode: currentTeacher.code,
        tSecret: currentTeacher.getSecret(),
        lCode: extending.code,
        endsAt: renewedEndsAt(extending, Date.now()),
        successTo: `${WELCOME_PATH}?renewed=1`,
      })
      return
    }
    submitCheckout({
      tCode: currentTeacher.code,
      tSecret: currentTeacher.getSecret(),
      licenseType: buying as
        | 'classroom'
        | 'classroompro'
        | 'school'
        | 'homeschool'
        | 'homeschoolpro',
      quantity: 1,
      singleSeats: effectiveCount ?? 0,
      term: selectedTerm,
      termLength:
        selectedEntry.termLength ?? (selectedTerm === 'sprint' ? 3 : 12),
      endsAt: selectedEntry.endsAt,
      successTo: WELCOME_PATH,
    })
  }

  return (
    // xm-tw scopes the Tailwind base rules; the rest of the app is still MUI.
    <div className='xm-tw mx-auto max-w-wide px-5 py-8 font-body text-ink'>
      {header}

      {flows.map((f) => (
        <FlowPlans
          key={f}
          flow={f}
          term={term}
          annualPrices={annualPrices}
          sprintPrices={sprintPrices}
          showPremium={hadPremium && !hasActivePro}
          seatsUsed={seatsUsedFor(f) ?? 0}
          onSelect={handleSelect}
          onTermChange={setTerm}
        />
      ))}

      {/* Schools and districts often need terms the self-serve plan cannot express. */}
      {primaryFlow === 'school' && <CustomPlanPanel />}

      <PurchasePanel
        details={details}
        transitionKey={`${selection?.flow}-${selection?.tier}-${selectedTerm}`}
        count={effectiveCount ?? 0}
        minCount={seats?.min ?? 0}
        onCountChange={setCount}
        planName={selection ? planNameFor(selection) : ''}
        onCheckout={handleCheckout}
        panelRef={panelRef}
      />

      <OtherOptions
        flow={primaryFlow}
        switchTo={switchTo}
        onSwitch={(next) => {
          setChosenFlow(next)
          setSelection(null)
        }}
      />
    </div>
  )
}

export default UpgradeLicense
